import React from 'react';
import './Calculus.css';

function Calculus() {
  return (
    <div className="calculus-container">
      <h2>Calculus</h2>
      
      <section>
        <h3>Introduction to Calculus</h3>
        <p>
          Calculus is a branch of mathematics that deals with rates of change and accumulation. It has two major branches:
          <ul>
            <li><b>Differential Calculus:</b> Concerning rates of change and slopes of curves.</li>
            <li><b>Integral Calculus:</b> Concerning accumulation of quantities and the areas under and between curves.</li>
          </ul>
          These two branches are related to each other by the fundamental theorem of calculus.
        </p>
      </section>

      <section>
        <h3>Limits</h3>
        <p>
          A limit is the value that a function or sequence "approaches" as the input or index approaches some value. Limits are essential to calculus and are used to define continuity, derivatives, and integrals.
        </p>
        <p>
          For example, the limit of <code>f(x) = x^2</code> as <code>x</code> approaches 2 is 4. This is written as:
          <pre>lim (x->2) x^2 = 4</pre>
        </p>
      </section>

      <section>
        <h3>Derivatives</h3>
        <p>
          The derivative of a function of a real variable measures the sensitivity to change of the function value (output value) with respect to a change in its argument (input value).
        </p>
        <p>
          The derivative of a function <code>f(x)</code> at a point <code>x</code> is the slope of the tangent line to the graph of <code>f(x)</code> at that point.
        </p>
        <h4>Common Derivative Rules:</h4>
        <ul>
          <li><b>Power Rule:</b> If <code>f(x) = x^n</code>, then <code>f'(x) = n*x^(n-1)</code></li>
          <li><b>Product Rule:</b> If <code>h(x) = f(x)g(x)</code>, then <code>h'(x) = f'(x)g(x) + f(x)g'(x)</code></li>
          <li><b>Quotient Rule:</b> If <code>h(x) = f(x)/g(x)</code>, then <code>h'(x) = (f'(x)g(x) - f(x)g'(x)) / (g(x))^2</code></li>
          <li><b>Chain Rule:</b> If <code>h(x) = f(g(x))</code>, then <code>h'(x) = f'(g(x)) * g'(x)</code></li>
        </ul>
      </section>

      <section>
        <h3>Integrals</h3>
        <p>
          Integration is the reverse process of differentiation. It is often used to find the area under a curve.
        </p>
        <h4>The Fundamental Theorem of Calculus</h4>
        <p>
          The fundamental theorem of calculus states that if a function <code>f</code> is continuous on the interval <code>[a, b]</code> and if <code>F</code> is a function whose derivative is <code>f</code> on the interval <code>(a, b)</code>, then
          <pre>∫[a,b] f(x) dx = F(b) - F(a)</pre>
        </p>
        <h4>Applications of Integrals</h4>
        <ul>
          <li>Finding the area between curves.</li>
          <li>Calculating the volume of a solid.</li>
          <li>Determining the arc length of a curve.</li>
        </ul>
      </section>
      
      <section>
        <h3>Further Learning</h3>
        <p>To continue your journey into calculus, you should explore:</p>
        <ul>
          <li><b>Multivariable Calculus:</b> Extends calculus to functions of several variables.</li>
          <li><b>Differential Equations:</b> Studies equations that involve an unknown function and its derivatives.</li>
        </ul>
      </section>
    </div>
  );
}

export default Calculus;
